import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTheme } from '@mui/material';
import { useGetCompanyTheme } from 'ogp/hooks';
import { CompanyTheme } from 'shared/contexts/theme-provider';
import type { UseSettingsEditThemeFormResult } from './use-settings-edit-theme-form.types';
import type { SettingEditThemeFormData } from '../setting-edit-theme/setting-edit-theme-form.types';

const useSettingsEditThemeForm = (): UseSettingsEditThemeFormResult => {
  const theme = useTheme();
  const themeQuery = useGetCompanyTheme();

  const originalTheme = useRef<CompanyTheme>({
    primaryColor: theme.palette.primary.main,
    secondaryColor: theme.palette.secondary.main,
    primaryAccent: theme.palette.lwPrimaryAccentLight[100],
    secondaryAccent: theme.palette.lwSecondaryAccentLight[100],
    logo: themeQuery.data?.logo ?? undefined,
  });

  const formMethods = useForm<SettingEditThemeFormData>({
    defaultValues: {
      colors: {
        primaryColor: theme.palette.primary.main,
        secondaryColor: theme.palette.secondary.main,
        primaryAccent: theme.palette.lwPrimaryAccentLight[100],
        secondaryAccent: theme.palette.lwSecondaryAccentLight[100],
      },
      logo: {
        portalLogoWide: themeQuery.data?.logo?.portalLogoWideUrl ?? null,
        portalLogoSquare: themeQuery.data?.logo?.portalLogoSquareUrl ?? null,
      },
    },
  });

  const hasInitializedLogo = useRef(false);
  useEffect(() => {
    if (!themeQuery.data || hasInitializedLogo.current) {
      return;
    }
    hasInitializedLogo.current = true;
    formMethods.reset((values) => ({
      ...values,
      logo: {
        portalLogoWide: themeQuery.data?.logo?.portalLogoWideUrl ?? null,
        portalLogoSquare: themeQuery.data?.logo?.portalLogoSquareUrl ?? null,
      },
    }));
    originalTheme.current = {
      ...originalTheme.current,
      logo: themeQuery.data?.logo ?? undefined,
    };
  }, [themeQuery.data, formMethods]);

  return { formMethods, originalThemeRef: originalTheme };
};

export { useSettingsEditThemeForm };
